'use client';

import posthog from 'posthog-js';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { Modal } from './Modal';
import { Button } from './Button';
import { Crown, Check, HelpCircle } from 'lucide-react';
import { FREE_TIER_ASSET_LIMIT } from '@/types/subscription';
import { cn } from '@/lib/cn';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const features = [
  'Unlimited assets',
  'Article sentiment & summaries',
  'ETF sector & regional allocations',
  'ETF holdings breakdown',
  '50 monthly credits for Research mode',
  'Early access to new features',
  'Cancel anytime',
];

export function UpgradeModal({ isOpen, onClose }: UpgradeModalProps) {
  const router = useRouter();

  const handleUpgrade = () => {
    posthog.capture('upgrade_button_clicked', {
      trigger: 'upgrade_modal',
      price: 5,
      currency: 'CAD',
    });
    onClose();
    router.push('/checkout');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Upgrade to Premium">
      <div className="text-center">
        <div className={cn(
          "mx-auto mb-4 w-16 h-16 rounded-full",
          "bg-gradient-to-br from-yellow-500 to-orange-500",
          "flex items-center justify-center"
        )}>
          <Crown className="w-8 h-8 text-white" />
        </div>

        <h3 className="text-lg font-semibold text-[var(--color-text)] mb-2">
          You&apos;ve reached the free limit
        </h3>
        <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
          Free accounts are limited to {FREE_TIER_ASSET_LIMIT} assets. Upgrade
          to Premium for unlimited portfolio tracking.
        </p>

        <div className={cn("rounded-xl p-4 mb-6 text-left")} style={{ backgroundColor: 'var(--color-bg)' }}>
          <div className="text-sm font-medium text-[var(--color-text)] mb-3">
            Premium includes:
          </div>
          <ul className="space-y-2 text-sm" style={{ color: 'var(--color-text-primary)' }}>
            {features.map((feature, i) => (
              <motion.li
                key={feature}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.08 }}
                className="flex items-center gap-2"
              >
                <Check size={16} className="text-green-500 flex-shrink-0" />
                {feature}
              </motion.li>
            ))}
          </ul>
        </div>

        <div className="mb-6">
          <span className="text-3xl font-bold text-[var(--color-text)]">$5</span>
          <span style={{ color: 'var(--color-text-secondary)' }}> CAD/month</span>
          <span style={{ color: 'var(--color-text-secondary)' }}> or </span>
          <span className="text-3xl font-bold text-[var(--color-text)]">$49.99</span>
          <span style={{ color: 'var(--color-text-secondary)' }}> CAD/year</span>
        </div>

        <div className="flex gap-3">
          <Button variant="secondary" onClick={onClose} className="flex-1">
            Maybe Later
          </Button>
          <Button onClick={handleUpgrade} className="flex-1">
            Upgrade Now
          </Button>
        </div>

        <Link
          href="/faq"
          onClick={onClose}
          className="mt-4 inline-flex items-center gap-1.5 text-sm transition-colors" style={{ color: 'var(--color-text-secondary)' }}
        >
          <HelpCircle size={14} />
          Have questions? View FAQ
        </Link>
      </div>
    </Modal>
  );
}
